/* CUSTOM — UGC video carousel
   Keeps at most a handful of videos decoding at once by pausing anything off screen. */

class UgcCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ugc-track]');
    this.cards = Array.from(this.querySelectorAll('[data-ugc-card]'));

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.desktop = window.matchMedia('(min-width: 750px)');

    // Cards the visitor has paused by hand stay paused — scrolling must not
    // quietly restart them.
    this.userPaused = new WeakSet();
    this.ratios = new Map();

    this.setAttribute('data-resolved-playback', this.reducedMotion ? 'manual' : 'auto');

    this.#bindArrows();
    this.#bindCards();
    this.#observe();

    this.desktop.addEventListener('change', () => this.#sync());
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  #video(card) {
    return card.querySelector('[data-ugc-video]');
  }

  #play(card) {
    const video = this.#video(card);
    if (!video || this.userPaused.has(card)) return;
    const attempt = video.play();
    if (attempt?.catch) attempt.catch(() => {});
  }

  #pause(card) {
    const video = this.#video(card);
    if (video && !video.paused) video.pause();
  }

  /** Desktop plays everything on screen; touch plays only the card most in
   *  view, since the carousel shows roughly one at a time there. */
  #sync() {
    if (this.reducedMotion) return;

    if (this.desktop.matches) {
      this.cards.forEach((card) => {
        if ((this.ratios.get(card) || 0) > 0.4) this.#play(card);
        else this.#pause(card);
      });
      return;
    }

    let best = null;
    let bestRatio = 0;
    this.cards.forEach((card) => {
      const ratio = this.ratios.get(card) || 0;
      if (ratio > bestRatio) {
        bestRatio = ratio;
        best = card;
      }
    });

    this.cards.forEach((card) => {
      if (card === best && bestRatio > 0.5) this.#play(card);
      else this.#pause(card);
    });
  }

  #bindCards() {
    this.cards.forEach((card) => {
      const video = this.#video(card);
      if (!video) return;

      const toggle = () => {
        if (video.paused) {
          this.userPaused.delete(card);
          this.#play(card);
        } else {
          this.userPaused.add(card);
          this.#pause(card);
        }
      };

      // The whole frame is tappable on touch; the button carries it on desktop.
      card.querySelector('.ugc-card__media')?.addEventListener('click', (event) => {
        event.preventDefault();
        toggle();
      });

      video.addEventListener('playing', () => card.setAttribute('data-playing', ''));
      video.addEventListener('pause', () => card.removeAttribute('data-playing'));
    });
  }

  #observe() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => this.ratios.set(entry.target, entry.intersectionRatio));
        this.#sync();
      },
      { root: null, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    this.cards.forEach((card) => this.observer.observe(card));
  }

  #bindArrows() {
    const scope = this.closest('.ugc') || document;
    const prev = scope.querySelector('[data-ugc-prev]');
    const next = scope.querySelector('[data-ugc-next]');
    if (!prev || !next || !this.track) return;

    const step = () => {
      const card = this.cards[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap || '16');
      return card ? card.getBoundingClientRect().width + gap : 280;
    };

    // No `behavior` key on purpose — the track's CSS scroll-behavior governs it.
    prev.addEventListener('click', () => this.track.scrollBy({ left: -step() }));
    next.addEventListener('click', () => this.track.scrollBy({ left: step() }));

    const sync = () => {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      prev.disabled = this.track.scrollLeft <= 2;
      next.disabled = this.track.scrollLeft >= max;
    };

    this.track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    sync();
    requestAnimationFrame(sync);
  }
}

if (!customElements.get('ugc-carousel')) {
  customElements.define('ugc-carousel', UgcCarousel);
}
